import { Download, Edit, Plus, RefreshCw, Search, Trash2, Upload, Users } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ConfirmationModal from "../ConfirmationModal";
import { useToast } from "../../contexts/ToastContext";
import { adminProductsV3Service } from "../../services/adminProductsV3Service";
import httpService from "../../services/httpService";
import { subscriptionService } from "../../services/subscriptionService";
import {
  getAccessSummary,
  SubscriptionSettings,
  SubscriptionWithRelations,
} from "../../types/subscription";
import { buildCSV, triggerCSVDownload } from "../../utils/csvExport";
import { formatUtcDate } from "../../utils/dateUtils";
import AddSubscriberModal from "./AddSubscriberModal";
import BulkAddUsersModal from "./BulkAddUsersModal";
import EditSubscriptionModal from "./EditSubscriptionModal";

type AccessFilter = "all" | "All" | "Specific" | "None";
type SourceFilter = "all" | "external" | "organic";

const PAGE_SIZE = 50;

interface ProviderInfo {
  id: number;
  name: string;
}

interface ProviderItem {
  id: number;
  title: string;
  visibility: string;
}

interface ProviderSubscriptionsManagerProps {
  providerId: number | null;
  providerName?: string;
}

function isExternalSubscription(subscription: SubscriptionWithRelations): boolean {
  return subscription.subscriptionSettings.isSubscribedExternallyToProvider === true;
}

const AccessBadge: React.FC<{ ids: number[] }> = ({ ids }) => {
  const summary = getAccessSummary(ids);
  const className = summary === "All"
    ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
    : summary === "None"
      ? "border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-800 dark:bg-rp-surface-raised dark:text-gray-400"
      : "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";

  return (
    <span className={`inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${className}`}>
      {summary}
    </span>
  );
};

const SourceBadge: React.FC<{ subscription: SubscriptionWithRelations }> = ({ subscription }) => (
  <span
    className={`inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
      isExternalSubscription(subscription)
        ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
        : "border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-800 dark:bg-rp-surface-raised dark:text-gray-400"
    }`}
  >
    {isExternalSubscription(subscription) ? "External" : "In-app / Strategy"}
  </span>
);

const ProviderSubscriptionsManager: React.FC<ProviderSubscriptionsManagerProps> = ({
  providerId,
  providerName,
}) => {
  const toast = useToast();
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithRelations[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providerInfo, setProviderInfo] = useState<ProviderInfo | null>(
    providerId && providerName ? { id: providerId, name: providerName } : null
  );
  const [page, setPage] = useState(1);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBackfillModal, setShowBackfillModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionWithRelations | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [accessFilter, setAccessFilter] = useState<AccessFilter>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [nonLowercaseOnly, setNonLowercaseOnly] = useState(false);

  const [providerPortfolios, setProviderPortfolios] = useState<ProviderItem[]>([]);
  const [providerBaskets, setProviderBaskets] = useState<ProviderItem[]>([]);
  const [contentLoading, setContentLoading] = useState(false);

  const validProviderId = Number.isInteger(providerId) && Number(providerId) > 0
    ? Number(providerId)
    : null;

  const displayProviderName = providerInfo?.name || providerName || (validProviderId ? `provider ID: ${validProviderId}` : "selected provider");

  const fetchProviderInfo = useCallback(async () => {
    if (!validProviderId) {
      setProviderInfo(null);
      return;
    }
    if (providerName) {
      setProviderInfo({ id: validProviderId, name: providerName });
      return;
    }

    try {
      const response = await httpService.get<{ provider: ProviderInfo }>(`/providers/${validProviderId}`);
      setProviderInfo(response.data.provider);
    } catch (err) {
      console.error("Error fetching provider info:", err);
    }
  }, [providerName, validProviderId]);

  const fetchSubscriptions = useCallback(async () => {
    if (!validProviderId) {
      setSubscriptions([]);
      setError("Select a provider to manage subscriptions.");
      return;
    }

    try {
      setLoading(true);
      const data = await subscriptionService.listProviderSubscribers(validProviderId);
      setSubscriptions(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError("Failed to load subscribers. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [validProviderId]);

  const fetchProviderContent = useCallback(async () => {
    if (!validProviderId) return;

    try {
      setContentLoading(true);
      const [portfolios, baskets] = await Promise.all([
        adminProductsV3Service.listAllPortfolios(validProviderId),
        adminProductsV3Service.listAllBaskets(validProviderId),
      ]);
      setProviderPortfolios(
        portfolios
          .filter((portfolio) => portfolio.visibility === "SUBSCRIBER")
          .map((portfolio) => ({ id: portfolio.id, title: portfolio.title, visibility: portfolio.visibility }))
      );
      setProviderBaskets(
        baskets
          .filter((basket) => basket.visibility === "SUBSCRIBER")
          .map((basket) => ({ id: basket.id, title: basket.title, visibility: basket.visibility }))
      );
    } catch (err) {
      console.error("Error fetching provider content:", err);
      setProviderPortfolios([]);
      setProviderBaskets([]);
    } finally {
      setContentLoading(false);
    }
  }, [validProviderId]);

  useEffect(() => {
    setPage(1);
    setSearchQuery("");
    setAccessFilter("all");
    setSourceFilter("all");
    setNonLowercaseOnly(false);
  }, [validProviderId]);

  useEffect(() => {
    setPage(1);
  }, [accessFilter, nonLowercaseOnly, searchQuery, sourceFilter]);

  useEffect(() => {
    fetchProviderInfo();
    fetchSubscriptions();
    fetchProviderContent();
  }, [fetchProviderInfo, fetchProviderContent, fetchSubscriptions]);

  const handleAddSubscriber = async (
    userId: string,
    settings: SubscriptionSettings
  ): Promise<void> => {
    if (!validProviderId) return;
    await subscriptionService.createSubscription(validProviderId, userId, settings);
    toast.success("Subscriber added successfully");
    await fetchSubscriptions();
    setShowAddModal(false);
  };

  const handleEditSubscription = async (
    subscriptionId: number,
    settings: SubscriptionSettings
  ): Promise<void> => {
    if (!validProviderId) return;
    await subscriptionService.updateProviderSubscription(validProviderId, subscriptionId, {
      subscriptionSettings: settings,
    });
    toast.success("Subscription updated successfully");
    await fetchSubscriptions();
    setShowEditModal(false);
    setSelectedSubscription(null);
  };

  const handleDeleteSubscription = async () => {
    if (!validProviderId || !selectedSubscription) return;

    try {
      setDeleteLoading(true);
      const result = await subscriptionService.deleteProviderSubscription(
        validProviderId,
        selectedSubscription.id
      );
      toast.success(
        result.action === "downgraded"
          ? "Subscription downgraded because active strategies exist"
          : "Subscription deleted successfully"
      );
      await fetchSubscriptions();
      setShowDeleteModal(false);
      setSelectedSubscription(null);
    } catch (err) {
      console.error("Error deleting subscription:", err);
      toast.error("Failed to delete subscription");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBackfillSubscriptions = async () => {
    if (!validProviderId) return;

    try {
      setBackfillLoading(true);
      const result = await subscriptionService.backfillProviderSubscriptions(validProviderId);
      toast.success(`Backfill complete: ${result.updated} updated, ${result.skipped} skipped`);
      await fetchSubscriptions();
      setShowBackfillModal(false);
    } catch (err) {
      console.error("Error backfilling subscriptions:", err);
      toast.error("Failed to backfill subscriptions");
    } finally {
      setBackfillLoading(false);
    }
  };

  const filteredSubscriptions = useMemo(() => {
    let result = subscriptions;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (subscription) =>
          subscription.subscriptionUser.email.toLowerCase().includes(query) ||
          (subscription.subscriptionUser.name && subscription.subscriptionUser.name.toLowerCase().includes(query))
      );
    }

    if (accessFilter !== "all") {
      result = result.filter(
        (subscription) =>
          getAccessSummary(subscription.subscriptionSettings.portfolios) === accessFilter ||
          getAccessSummary(subscription.subscriptionSettings.baskets) === accessFilter ||
          getAccessSummary(subscription.subscriptionSettings.portfolios).startsWith(accessFilter) ||
          getAccessSummary(subscription.subscriptionSettings.baskets).startsWith(accessFilter)
      );
    }

    if (sourceFilter !== "all") {
      result = result.filter((subscription) =>
        sourceFilter === "external" ? isExternalSubscription(subscription) : !isExternalSubscription(subscription)
      );
    }

    if (nonLowercaseOnly) {
      result = result.filter(
        (subscription) => subscription.subscriptionUser.email !== subscription.subscriptionUser.email.toLowerCase()
      );
    }

    return result;
  }, [accessFilter, nonLowercaseOnly, searchQuery, sourceFilter, subscriptions]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredSubscriptions.length / PAGE_SIZE));
  }, [filteredSubscriptions.length]);

  const paginatedSubscriptions = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return filteredSubscriptions.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredSubscriptions, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleDownloadCSV = useCallback(() => {
    const headers = ["email", "name", "portfolioAccess", "basketAccess", "source", "createdAt", "updatedAt"];
    const rows = filteredSubscriptions.map((subscription) => [
      subscription.subscriptionUser.email,
      subscription.subscriptionUser.name || "",
      getAccessSummary(subscription.subscriptionSettings.portfolios),
      getAccessSummary(subscription.subscriptionSettings.baskets),
      isExternalSubscription(subscription) ? "External" : "Organic",
      formatUtcDate(subscription.createdAtUtc),
      formatUtcDate(subscription.updatedAtUtc),
    ]);
    const csv = buildCSV(headers, rows);
    const safeProviderName = displayProviderName.replace(/\s+/g, "_");
    const filename = `subscribers_${safeProviderName}_${new Date().toISOString().split("T")[0]}.csv`;
    triggerCSVDownload(csv, filename);
  }, [displayProviderName, filteredSubscriptions]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-bold leading-tight text-gray-900 dark:text-white">
            Provider Roster
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {displayProviderName} - {subscriptions.length} total subscribers
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={fetchSubscriptions}
            disabled={loading || !validProviderId}
            className="flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowBackfillModal(true)}
            disabled={backfillLoading || !validProviderId}
            className="flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${backfillLoading ? "animate-spin" : ""}`} />
            Backfill External
          </button>
          <button
            type="button"
            onClick={() => setShowBulkModal(true)}
            disabled={!validProviderId}
            className="flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
          >
            <Upload className="mr-2 h-4 w-4" />
            Sync Roster
          </button>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            disabled={!validProviderId}
            className="flex items-center rounded-lg bg-rp-amber px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Subscriber
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search email or name..."
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white dark:placeholder-gray-500"
            />
          </div>
          <select
            value={accessFilter}
            onChange={(event) => setAccessFilter(event.target.value as AccessFilter)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white"
          >
            <option value="all">All Access Types</option>
            <option value="All">Full Access</option>
            <option value="Specific">Specific</option>
            <option value="None">No Access</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(event) => setSourceFilter(event.target.value as SourceFilter)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white"
          >
            <option value="all">All Sources</option>
            <option value="external">External</option>
            <option value="organic">In-app / Strategy</option>
          </select>
          <label className="flex cursor-pointer select-none items-center gap-2">
            <input
              type="checkbox"
              checked={nonLowercaseOnly}
              onChange={(event) => setNonLowercaseOnly(event.target.checked)}
              className="rounded border-gray-300 text-rp-amber focus:ring-rp-amber dark:border-rp-separator"
            />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Non-lowercase emails
            </span>
          </label>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Showing {paginatedSubscriptions.length} of {filteredSubscriptions.length} matching, {subscriptions.length} total
          </span>
          <button
            type="button"
            onClick={handleDownloadCSV}
            disabled={filteredSubscriptions.length === 0}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
          >
            <Download className="h-4 w-4" />
            Download CSV
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface">
        {error ? (
          <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        ) : loading ? (
          <div className="space-y-3 p-4">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-16 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-800" />
            ))}
          </div>
        ) : filteredSubscriptions.length === 0 && subscriptions.length === 0 ? (
          <div className="py-24 text-center">
            <Users className="mx-auto mb-3 h-8 w-8 text-gray-300 dark:text-gray-600" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              No subscribers yet
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Add subscribers to give users access to subscriber-only content.
            </p>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="mt-4 inline-flex items-center rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add First Subscriber
            </button>
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No subscribers match the current search or filter criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-rp-separator">
              <thead className="bg-gray-50 dark:bg-rp-surface-sunken">
                <tr>
                  {["User", "Portfolio Access", "Basket Access", "Source", "Created", "Updated", "Actions"].map((header) => (
                    <th
                      key={header}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-rp-separator dark:bg-black">
                {paginatedSubscriptions.map((subscription) => (
                  <tr key={subscription.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-rp-surface">
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {subscription.subscriptionUser.email}
                      </div>
                      {subscription.subscriptionUser.name && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {subscription.subscriptionUser.name}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <AccessBadge ids={subscription.subscriptionSettings.portfolios} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <AccessBadge ids={subscription.subscriptionSettings.baskets} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <SourceBadge subscription={subscription} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {formatUtcDate(subscription.createdAtUtc)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {formatUtcDate(subscription.updatedAtUtc)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setShowEditModal(true);
                          }}
                          className="rounded p-1 text-rp-amber transition-colors hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                          title="Edit subscription"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setShowDeleteModal(true);
                          }}
                          className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          title="Delete subscription"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || loading}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={page >= totalPages || loading}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <AddSubscriberModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddSubscriber}
        providerPortfolios={providerPortfolios}
        providerBaskets={providerBaskets}
        existingSubscriberIds={subscriptions.map((subscription) => subscription.subscriptionUserId)}
        contentLoading={contentLoading}
      />

      <BulkAddUsersModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSuccess={fetchSubscriptions}
        initialProviderId={validProviderId}
        lockProvider
      />

      {selectedSubscription && (
        <EditSubscriptionModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedSubscription(null);
          }}
          onSubmit={handleEditSubscription}
          subscription={selectedSubscription}
          providerPortfolios={providerPortfolios}
          providerBaskets={providerBaskets}
          contentLoading={contentLoading}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSubscription(null);
        }}
        onConfirm={handleDeleteSubscription}
        title="Remove Subscriber"
        message={`Remove ${selectedSubscription?.subscriptionUser.name || selectedSubscription?.subscriptionUser.email || "this subscriber"} from ${displayProviderName}?`}
        confirmText="Remove"
        isDestructive
        loading={deleteLoading}
      />

      <ConfirmationModal
        isOpen={showBackfillModal}
        onClose={() => setShowBackfillModal(false)}
        onConfirm={handleBackfillSubscriptions}
        title="Backfill External Subscriptions"
        message={`Run external subscription backfill across all subscriptions for ${displayProviderName}? This is a provider-wide mutation.`}
        confirmText="Run Backfill"
        loading={backfillLoading}
      />
    </div>
  );
};

export default ProviderSubscriptionsManager;
